/**
 * TanStack Query hooks for alert data and alert mutations.
 *
 * Query key: ['alerts']
 *
 * Mutations (acknowledgeAlert, dismissAlert) use onSuccess cache invalidation
 * so the alert list automatically refreshes after an action (Pattern 8.3).
 * Mutations do not use automatic retry (not safe for non-idempotent operations).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Alert } from '@/types';

// ── Fetch helpers ─────────────────────────────────────────────────────────────

async function fetchAlerts(): Promise<Alert[]> {
  const res = await fetch('/api/alerts');
  if (!res.ok) {
    throw new Error(`Failed to load alerts: ${res.status} ${res.statusText}`);
  }
  return res.json() as Promise<Alert[]>;
}

async function acknowledgeAlert(alertId: number): Promise<void> {
  const res = await fetch(`/api/alerts/${alertId}/acknowledge`, {
    method: 'POST',
  });
  if (!res.ok) {
    throw new Error(`Failed to acknowledge alert: ${res.status} ${res.statusText}`);
  }
}

async function dismissAlert(alertId: number): Promise<void> {
  const res = await fetch(`/api/alerts/${alertId}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error(`Failed to dismiss alert: ${res.status} ${res.statusText}`);
  }
}

// ── Query hooks ───────────────────────────────────────────────────────────────

/**
 * Fetches all active (unacknowledged) alerts.
 * Refetches on the given interval.
 * Also used by AppLayout to derive the unacknowledged count for the nav badge.
 */
export function useAlerts(refetchInterval: number) {
  return useQuery({
    queryKey: ['alerts'],
    queryFn: fetchAlerts,
    refetchInterval,
  });
}

/**
 * Mutation to acknowledge an alert by ID.
 * On success, invalidates the ['alerts'] query to trigger a fresh fetch.
 * Error is captured in mutation.error (Pattern 8.3).
 */
export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: acknowledgeAlert,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

/**
 * Mutation to dismiss (permanently delete) an alert by ID.
 * On success, invalidates the ['alerts'] query.
 * Error is captured in mutation.error (Pattern 8.3).
 */
export function useDismissAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: dismissAlert,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}
